import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { AppBar } from "features/appBar";
import { Map } from "features/map";
import { Icon } from "@trackcode/ui";
import styled from "styled-components";
import ModalPage from "containers/modal/ModalPage";
// TODO: Refactor SidebarRight and put it in chat feature
import SiderbarRightChat from "components/layout/sidebarRight/SiderbarRight";
import Footer from "components/layout/footer/Footer";
import {
  isPlatformSettingEnabled,
  resolveSidebarPlatformSettings,
  SETTING_SIDEBAR_EINSTELLUNGEN
} from "common/platformSettings";
import LayoutContext from "./LayoutContext";
import { ActionBarRenderer, Loading } from "./components";
import PlatformSidebar, {
  APP_BAR_HEIGHT,
  getModulePath,
  getPlatformSidebarWidth,
  readPlatformSidebarCollapsed
} from "./components/PlatformSidebar";

const initialState = {
  driver: {},
  showShipmentModal: false,
  showAdditionalModal: null,
  hide: false,
  addJobButtonDisabled: false,
  actionBar: null,
  activeMapMemberIds: []
};

const VIEW_MODE = {
  WORKBOOK: "workbook",
  LEGACY: "legacy"
};
const VIEW_QUERY_PARAM = "view";
const VIEW_STORAGE_KEY = "control_tower_view_mode";
const PANEL_SIZES_STORAGE_KEY = "control_tower_panel_sizes";
const DEFAULT_PANEL_SIZES = {
  left: 46
};
const MIN_LEFT_PANEL_PERCENT = 12;
const MIN_LEFT_PANEL_PX = 240;
const ORDER_LIST_STORAGE_KEY = "control_tower_order_list_collapsed";
const DRIVER_LIST_STORAGE_KEY = "control_tower_driver_list_collapsed";
const SIDEBAR_TRANSITION = "0.32s cubic-bezier(0.4, 0, 0.2, 1)";

class App extends Component {
  static propTypes = {
    /** indicates a reload while switching to another logisticcompany */
    reload: PropTypes.bool.isRequired,
    children: PropTypes.any,
    layoutV2: PropTypes.bool.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
      search: PropTypes.string
    }).isRequired,
    history: PropTypes.shape({
      replace: PropTypes.func.isRequired
    }).isRequired,
    user: PropTypes.shape({
      name: PropTypes.string,
      firstname: PropTypes.string,
      lastname: PropTypes.string,
      logisticcompany_id: PropTypes.number
    }),
    logisticCompanyId: PropTypes.number,
    managedLogisticCompanies: PropTypes.object,
    platformSettings: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object
    ]),
    homePlatformSettings: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object
    ]),
    handleLogout: PropTypes.func
  };

  static defaultProps = {
    user: null,
    logisticCompanyId: 0,
    managedLogisticCompanies: {},
    platformSettings: null,
    homePlatformSettings: null,
    handleLogout: null
  };

  constructor(props) {
    super(props);
    this.state = {
      ...initialState,
      viewMode: this.readInitialViewMode(),
      panelSizes: this.readInitialPanelSizes(),
      isOrderListCollapsed: this.readInitialOrderListCollapsed(),
      isDriverListCollapsed: this.readInitialDriverListCollapsed(),
      platformSidebarWidth: getPlatformSidebarWidth(
        readPlatformSidebarCollapsed()
      ),
      resizingSide: null
    };
    this.actionBarPropHandler = () => {};
    this.splitScreenRef = React.createRef();
  }

  componentDidMount() {
    if (this.props.location.pathname.startsWith("/scheduling")) {
      this.syncViewModeToStorageAndUrl(this.state.viewMode);
    }
    window.addEventListener("mousemove", this.handleResizeMove);
    window.addEventListener("mouseup", this.stopResize);
  }

  componentWillUnmount() {
    window.removeEventListener("mousemove", this.handleResizeMove);
    window.removeEventListener("mouseup", this.stopResize);
  }

  componentDidUpdate(prevProps) {
    const { reload, location } = this.props;
    // Reset state after switching to another logisticcompany.
    if (prevProps.reload === true && reload === false) {
      // componentWillReceiveProps is deprecated and in this case
      // it makes sense to use setState for this life cycle function.
      // See: https://reactjs.org/docs/react-component.html#componentdidupdate
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(prevState => ({
        ...initialState,
        viewMode: prevState.viewMode
      }));
    }

    if (prevProps.location.search !== location.search) {
      const viewModeFromUrl = this.readViewModeFromUrl();
      if (viewModeFromUrl && viewModeFromUrl !== this.state.viewMode) {
        this.setState({ viewMode: viewModeFromUrl });
      }
    }
  }

  readViewModeFromUrl = () => {
    const { location } = this.props;
    const params = new URLSearchParams(location.search || "");
    const urlView = params.get(VIEW_QUERY_PARAM);

    if (urlView === VIEW_MODE.WORKBOOK || urlView === VIEW_MODE.LEGACY) {
      return urlView;
    }

    return null;
  };

  readInitialViewMode = () => {
    const viewModeFromUrl = this.readViewModeFromUrl();
    if (viewModeFromUrl) {
      return viewModeFromUrl;
    }

    try {
      const storedViewMode = localStorage.getItem(VIEW_STORAGE_KEY);
      if (
        storedViewMode === VIEW_MODE.WORKBOOK ||
        storedViewMode === VIEW_MODE.LEGACY
      ) {
        return storedViewMode;
      }
    } catch (error) {
      // ignore storage errors
    }

    return VIEW_MODE.WORKBOOK;
  };

  syncViewModeToStorageAndUrl = viewMode => {
    const { history, location } = this.props;
    if (!location.pathname.startsWith("/scheduling")) {
      return;
    }

    try {
      localStorage.setItem(VIEW_STORAGE_KEY, viewMode);
    } catch (error) {
      // ignore storage errors
    }

    const params = new URLSearchParams(location.search || "");
    params.set(VIEW_QUERY_PARAM, viewMode);
    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : ""
    });
  };

  readInitialPanelSizes = () => {
    try {
      const saved = localStorage.getItem(PANEL_SIZES_STORAGE_KEY);
      if (!saved) {
        return DEFAULT_PANEL_SIZES;
      }
      const parsed = JSON.parse(saved);
      if (typeof parsed.left === "number") {
        return { left: parsed.left };
      }
    } catch (error) {
      // ignore storage errors
    }
    return DEFAULT_PANEL_SIZES;
  };

  savePanelSizes = panelSizes => {
    try {
      localStorage.setItem(PANEL_SIZES_STORAGE_KEY, JSON.stringify(panelSizes));
    } catch (error) {
      // ignore storage errors
    }
  };

  readInitialOrderListCollapsed = () => {
    try {
      return localStorage.getItem(ORDER_LIST_STORAGE_KEY) === "true";
    } catch (error) {
      return false;
    }
  };

  toggleOrderList = () => {
    this.setState(
      prevState => ({
        isOrderListCollapsed: !prevState.isOrderListCollapsed
      }),
      () => {
        try {
          localStorage.setItem(
            ORDER_LIST_STORAGE_KEY,
            String(this.state.isOrderListCollapsed)
          );
        } catch (error) {
          // ignore storage errors
        }
      }
    );
  };

  readInitialDriverListCollapsed = () => {
    try {
      return localStorage.getItem(DRIVER_LIST_STORAGE_KEY) === "true";
    } catch (error) {
      return false;
    }
  };

  toggleDriverList = () => {
    this.setState(
      prevState => ({
        isDriverListCollapsed: !prevState.isDriverListCollapsed
      }),
      () => {
        try {
          localStorage.setItem(
            DRIVER_LIST_STORAGE_KEY,
            String(this.state.isDriverListCollapsed)
          );
        } catch (error) {
          // ignore storage errors
        }
      }
    );
  };

  handlePlatformNavigate = moduleKey => {
    const path = getModulePath(moduleKey);
    if (path) {
      this.props.history.push(path);
    }
  };

  handlePlatformSidebarWidthChange = width => {
    this.setState({ platformSidebarWidth: width });
  };

  startResize = side => event => {
    event.preventDefault();
    this.setState({ resizingSide: side });
  };

  stopResize = () => {
    if (!this.state.resizingSide) {
      return;
    }
    this.setState({ resizingSide: null });
    this.savePanelSizes(this.state.panelSizes);
  };

  handleResizeMove = event => {
    const { resizingSide, panelSizes } = this.state;
    if (!resizingSide || !this.splitScreenRef.current) {
      return;
    }
    const bounds = this.splitScreenRef.current.getBoundingClientRect();
    const totalWidth = bounds.width || 1;
    const minLeft = Math.max(
      MIN_LEFT_PANEL_PERCENT,
      (MIN_LEFT_PANEL_PX / totalWidth) * 100
    );
    const minCenter = 20;

    if (resizingSide === "left") {
      const rawLeft = ((event.clientX - bounds.left) / totalWidth) * 100;
      const maxLeft = 100 - minCenter;
      const left = Math.max(minLeft, Math.min(rawLeft, maxLeft));
      this.setState({
        panelSizes: {
          ...panelSizes,
          left
        }
      });
    }
  };

  toggleViewMode = () => {
    const nextViewMode =
      this.state.viewMode === VIEW_MODE.WORKBOOK
        ? VIEW_MODE.LEGACY
        : VIEW_MODE.WORKBOOK;
    this.setState({ viewMode: nextViewMode });
    this.syncViewModeToStorageAndUrl(nextViewMode);
  };

  /**
   * Context function to render ActionBar props from everywhere.
   */
  setActionBarProps = props => {
    this.actionBarPropHandler(props);
  };

  handleDriverClicked = (driver, hide) => {
    this.setState({
      driver,
      hide
    });
  };

  handleDriverCenterOnMap = driver => {
    const driverId = driver?.id;

    if (typeof this.centerDriverOnMapHandler === "function") {
      this.centerDriverOnMapHandler(driverId);
    }

    if (this.state.viewMode === VIEW_MODE.WORKBOOK) {
      if (typeof this.workbookDriverFocusHandler === "function") {
        this.workbookDriverFocusHandler(driverId);
      }
      return;
    }

    if (typeof this.listDriverFocusHandler === "function") {
      this.listDriverFocusHandler(driverId);
    }
  };

  mapDriverClickHandler = null;
  centerDriverOnMapHandler = null;
  workbookDriverFocusHandler = null;
  listDriverFocusHandler = null;

  registerMapDriverClickHandler = handler => {
    this.mapDriverClickHandler = handler;
    this.forceUpdate();
  };

  registerCenterDriverOnMap = handler => {
    this.centerDriverOnMapHandler = handler;
  };

  registerWorkbookDriverFocusHandler = handler => {
    this.workbookDriverFocusHandler = handler;
  };

  registerListDriverFocusHandler = handler => {
    this.listDriverFocusHandler = handler;
  };

  handleMapDriverClick = driverId => {
    if (typeof this.mapDriverClickHandler === "function") {
      this.mapDriverClickHandler(driverId);
    }
  };

  setActiveMapMemberIds = memberIds => {
    const next = (
      Array.isArray(memberIds) ? memberIds : memberIds == null ? [] : [memberIds]
    )
      .map(id => Number(id))
      .filter(Number.isFinite);
    const unique = [...new Set(next)].sort((a, b) => a - b);
    const prev = this.state.activeMapMemberIds || [];
    if (
      prev.length === unique.length &&
      prev.every((id, index) => id === unique[index])
    ) {
      return;
    }
    this.setState({ activeMapMemberIds: unique });
  };

  handleCreateShipment = () => {
    this.setState({ showShipmentModal: true, addJobButtonDisabled: true });
  };

  handleModalOnClose = () => {
    this.setState({
      showShipmentModal: false,
      showAdditionalModal: null,
      addJobButtonDisabled: false
    });
  };

  handleMenuItemClicked = name => {
    switch (name) {
      case "settings":
        this.setState({ showAdditionalModal: name });
        break;
      default:
        console.log("coming soon");
        break;
    }
  };

  render() {
    const { setActionBarProps } = this;
    const {
      reload,
      children,
      layoutV2,
      location,
      user,
      logisticCompanyId,
      managedLogisticCompanies,
      platformSettings,
      homePlatformSettings,
      handleLogout
    } = this.props;

    const {
      driver,
      hide,
      showShipmentModal,
      showAdditionalModal,
      addJobButtonDisabled,
      viewMode,
      panelSizes,
      isOrderListCollapsed,
      isDriverListCollapsed,
      platformSidebarWidth,
      resizingSide,
      activeMapMemberIds
    } = this.state;
    const isSchedulingRoute = location.pathname.startsWith("/scheduling");
    const isPlatformContentRoute =
      location.pathname.startsWith("/management") ||
      location.pathname.startsWith("/admin/dashboard") ||
      location.pathname.startsWith("/collaboration") ||
      location.pathname.startsWith("/projects") ||
      location.pathname.startsWith("/internal");
    const isWorkbookView = viewMode === VIEW_MODE.WORKBOOK;
    const showWorkbookLayout = layoutV2 && isSchedulingRoute && isWorkbookView;
    // Wenn die "Einstellungen"-Setting deaktiviert ist, blenden wir auch die
    // operativen Bedienelemente (Auftrag hinzufügen + Sendungssuche) aus.
    // Maßgeblich ist dabei der übergeordnete (Heimat-)Mandant: Wechselt ein
    // Übermandant in einen Kundenmandanten, bleibt die Sichtbarkeit an seinen
    // eigenen Mandanten gekoppelt, nicht an den aktiven Kundenmandanten.
    const effectiveSidebarSettings = resolveSidebarPlatformSettings({
      user,
      logisticCompanyId,
      homePlatformSettings,
      platformSettings
    });
    const settingsSidebarEnabled = isPlatformSettingEnabled(
      effectiveSidebarSettings,
      SETTING_SIDEBAR_EINSTELLUNGEN
    );

    if (layoutV2) {
      const dispositionContent = (
        <SplitScreen
          ref={this.splitScreenRef}
          isResizing={Boolean(resizingSide)}
        >
          <SplitScreenContent
            style={{
              flex: isOrderListCollapsed
                ? "0 0 0px"
                : `0 0 ${panelSizes.left}%`,
              minWidth: 0,
              overflow: "hidden"
            }}
          >
            {!isOrderListCollapsed && children}
          </SplitScreenContent>
          <ResizeHandle
            $collapsed={isOrderListCollapsed}
            onMouseDown={
              isOrderListCollapsed ? undefined : this.startResize("left")
            }
          >
            <OrderListToggle
              type="button"
              onClick={this.toggleOrderList}
              title={
                isOrderListCollapsed
                  ? "Auftragsliste einblenden"
                  : "Auftragsliste ausblenden"
              }
            >
              <Icon
                name={isOrderListCollapsed ? "angle-right" : "angle-left"}
                size="sm"
              />
            </OrderListToggle>
          </ResizeHandle>
          <SplitScreenContent style={{ minWidth: 0 }}>
            <Map
              handleScopes={["drivers", "scheduling"]}
              showWencoMapLegend={showWorkbookLayout}
            />
          </SplitScreenContent>
          {isSchedulingRoute && (
            <WorkbookDriversContent
              collapsed={isDriverListCollapsed}
              style={{
                flex: isDriverListCollapsed ? "0 0 0px" : "0 0 220px"
              }}
            >
              <DriverListToggle
                onClick={this.toggleDriverList}
                title={
                  isDriverListCollapsed
                    ? "Fahrerliste einblenden"
                    : "Fahrerliste ausblenden"
                }
              >
                <Icon
                  name={isDriverListCollapsed ? "angle-left" : "angle-right"}
                  size="sm"
                />
              </DriverListToggle>
              {!isDriverListCollapsed && (
                <DriversContentArea>
                  <SiderbarRightChat
                    centerOnMapOnly
                    onDriverClick={this.handleDriverCenterOnMap}
                  />
                </DriversContentArea>
              )}
            </WorkbookDriversContent>
          )}
        </SplitScreen>
      );

      const v2Layout = (
        <PageWrapper>
          <LayoutContext.Provider
            value={{
              setActionBarProps,
              viewMode,
              toggleViewMode: this.toggleViewMode,
              registerMapDriverClickHandler:
                this.registerMapDriverClickHandler,
              registerCenterDriverOnMap: this.registerCenterDriverOnMap,
              registerWorkbookDriverFocusHandler:
                this.registerWorkbookDriverFocusHandler,
              registerListDriverFocusHandler:
                this.registerListDriverFocusHandler,
              onMapDriverClick: this.handleMapDriverClick,
              activeMapMemberIds,
              setActiveMapMemberIds: this.setActiveMapMemberIds
            }}
          >
            <PlatformShell>
              <PlatformSidebar
                user={user}
                logisticCompanyId={logisticCompanyId}
                managedLogisticCompanies={managedLogisticCompanies}
                platformSettings={platformSettings}
                homePlatformSettings={homePlatformSettings}
                pathname={location.pathname}
                onNavigate={this.handlePlatformNavigate}
                onSettingsClick={() => this.handleMenuItemClicked("settings")}
                onLogout={handleLogout}
                onWidthChange={this.handlePlatformSidebarWidthChange}
              />
              <PlatformMain>
                {isPlatformContentRoute ? (
                  children
                ) : (
                  <DispositionModuleContent>
                    <AppBar
                      sidebarOffset={platformSidebarWidth}
                      showSettingsButton={false}
                      showProfileButton={false}
                      showLogo={false}
                      showTrackingSearch={settingsSidebarEnabled}
                      showCreateShipmentButton={settingsSidebarEnabled}
                      handleCreateShipment={this.handleCreateShipment}
                      handleMenuItemClicked={this.handleMenuItemClicked}
                      shipmentButtonDisabled={addJobButtonDisabled}
                    />
                    <ActionBarRenderer
                      handler={handler => {
                        this.actionBarPropHandler = handler;
                      }}
                    />
                    <DispositionBody>{dispositionContent}</DispositionBody>
                  </DispositionModuleContent>
                )}
              </PlatformMain>
            </PlatformShell>
          </LayoutContext.Provider>
        </PageWrapper>
      );

      return (
        <div>
          <Wrapper navbarHeight={0}>
            <Content className="modal-container">
              {reload ? <Loading /> : v2Layout}
              {reload === false && (
                <ModalPage
                  showShipmentModal={showShipmentModal}
                  showAdditionalModal={showAdditionalModal}
                  handleOnClose={this.handleModalOnClose}
                />
              )}
            </Content>
          </Wrapper>
          {reload === false && <Footer chatPartner={driver} hide={hide} />}
        </div>
      );
    }

    // backward compatibility, just render the children
    let layout = children;

    return (
      <div>
        <AppBar
          handleCreateShipment={this.handleCreateShipment}
          handleMenuItemClicked={this.handleMenuItemClicked}
          shipmentButtonDisabled={addJobButtonDisabled}
        />
        <Wrapper navbarHeight={51}>
          <Content className="modal-container">
            {reload ? <Loading /> : layout}
            {reload === false && (
              <ModalPage
                showShipmentModal={showShipmentModal}
                showAdditionalModal={showAdditionalModal}
                handleOnClose={this.handleModalOnClose}
              />
            )}
          </Content>
          {!showWorkbookLayout && (
            <SidebarRight>
              {reload === false && (
                <SiderbarRightChat
                  handleDriverClicked={this.handleDriverClicked}
                />
              )}
            </SidebarRight>
          )}
        </Wrapper>

        {reload === false && <Footer chatPartner={driver} hide={hide} />}
      </div>
    );
  }
}

const Wrapper = styled.div`
  position: absolute;
  display: flex;
  top: ${props => props.navbarHeight || 0}px;
  right: 0;
  bottom: 0;
  left: 0;
`;

const Content = styled.div`
  display: flex;
  flex: 5;
`;

const SidebarRight = styled.div`
  flex: 1;
  max-width: 220px;
  background: #f7f6f2;
  border-left: 1px solid #d6d7da;
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
`;

const SplitScreen = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  user-select: ${({ isResizing }) => (isResizing ? "none" : "auto")};
`;

const PlatformShell = styled.div`
  display: flex;
  flex: 1;
  height: 100%;
  min-height: 0;
`;

const PlatformMain = styled.div`
  flex: 1;
  height: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: flex ${SIDEBAR_TRANSITION};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const DispositionModuleContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding-top: ${APP_BAR_HEIGHT}px;
`;

const DispositionBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

export const SplitScreenContent = styled.div`
  display: flex;
  flex: ${props => props.flex || 1};
  flex-direction: column;
  border-right: 1px solid #d6d7da;
  height: 100%;
  min-width: 0;
`;

const WorkbookDriversContent = styled(SplitScreenContent)`
  position: relative;
  min-width: 0;
  background: #f7f6f2;
  border-left: ${({ collapsed }) => (collapsed ? "0" : "1px solid #d6d7da")};
  box-shadow: ${({ collapsed }) =>
    collapsed ? "none" : "inset 1px 0 0 #e6e6e6"};
  max-width: 220px;
  overflow: visible;
`;

const DriverListToggle = styled.button`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translate(-100%, -50%);
  width: 18px;
  height: 74px;
  border: 1px solid #d6d7da;
  border-right: 0;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  background: #ededed;
  color: #5f5f5f;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  z-index: 2;
  box-shadow: -1px 0 2px rgba(0, 0, 0, 0.08);
  &:hover {
    background: #e4e4e4;
    color: #3f3f3f;
  }
`;

const DriversContentArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 0;
  & > * {
    width: 100%;
    min-width: 0;
  }
`;

const ResizeHandle = styled.div`
  position: relative;
  flex: 0 0 6px;
  width: 6px;
  cursor: ${({ $collapsed }) => ($collapsed ? "default" : "col-resize")};
  background: #e1e1e1;
  border-left: 1px solid #d6d7da;
  border-right: 1px solid #d6d7da;
  &:hover {
    background: #d4d4d4;
  }
`;

const OrderListToggle = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate(100%, -50%);
  width: 18px;
  height: 74px;
  border: 1px solid #d6d7da;
  border-left: 0;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  background: #ededed;
  color: #5f5f5f;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  z-index: 2;
  box-shadow: 1px 0 2px rgba(0, 0, 0, 0.08);
  &:hover {
    background: #e4e4e4;
    color: #3f3f3f;
  }
`;

export default withRouter(App);
